// LeaderboardDetailPage — sticky shell + 5-tab nav for a single leaderboard.
//
// Ports the "Overview" tab from docs/mockups/leaderboard-detail.html (L600–793).
// Tabs other than `overview` render a placeholder until T12–T15 land.
//
// URL contract: `?tab=overview|cycles|rewards|audit|settings`. The current tab
// lives in the query string so deep links + browser back/forward work and the
// PageHeader's breadcrumb stays stable across tab switches.

import { useMemo } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MoreVertical } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { ApiError, describeError } from "../../lib/errors";
import { formatRelativeShort } from "../../lib/format";
import { useProject } from "../projects/useProject";
import { normalizeConfig } from "../projects/config/defaults";
import {
  getLeaderboard,
  lbKeys,
  listAudit,
  listCycleTop,
  listCycles,
} from "./api";
import type {
  AuditEntry,
  Cycle,
  Entry,
  Leaderboard,
} from "./types";
import { countrySplitAllowed, cycleShape, describeSegments } from "./segments";
import type { SegmentDescriptor } from "./segments";
import { formatWindowLength } from "./windowLength";
import { useBucketSummary } from "./useBucketSummary";
import { SegmentCell } from "./SegmentCell";
import { SegmentNote } from "./SegmentNote";
import { ActorCell, EntityCell, EntityLink } from "./EntityCell";
import { entityKindOf } from "./entityKind";
import { ScoreFlow } from "./ScoreFlow";
import { AuditTab } from "./tabs/AuditTab";
import { CyclesTab } from "./tabs/CyclesTab";
import { RewardsTab } from "./tabs/RewardsTab";
import { SettingsTab } from "./tabs/SettingsTab";
import styles from "./LeaderboardDetailPage.module.css";

// ──────────────────────────────────────────────────────────────────────────────
// Badge helpers — local copies of LeaderboardsListPage.tsx::windowBadgeFor /
// operatorBadgeFor / scopeLabelFor. The list page does not export them and is
// locked by this task's brief, so we duplicate verbatim here.
// TODO(T11+): unify with LeaderboardsListPage badge helpers in lib/leaderboardBadges.tsx.
// ──────────────────────────────────────────────────────────────────────────────

function describeCron(cron: string | undefined): string {
  if (!cron) return "recurring";
  const trimmed = cron.trim();
  const parts = trimmed.split(/\s+/);
  if (parts.length === 5) {
    const [, , dom, mon, dow] = parts;
    if (dom === "*" && mon === "*" && /^[A-Z0-6]+$/i.test(dow) && dow !== "*") {
      const map: Record<string, string> = {
        "0": "SUN", "7": "SUN",
        "1": "MON", "2": "TUE", "3": "WED", "4": "THU", "5": "FRI", "6": "SAT",
        SUN: "SUN", MON: "MON", TUE: "TUE", WED: "WED", THU: "THU", FRI: "FRI", SAT: "SAT",
      };
      const day = map[dow.toUpperCase()] ?? dow.toUpperCase();
      return `weekly @ ${day}`;
    }
    if (dow === "*" && mon === "*" && /^\d+$/.test(dom)) {
      return `monthly @ ${dom}`;
    }
    if (dow === "*" && dom === "*" && mon === "*") {
      return "daily";
    }
  }
  return trimmed;
}

interface WindowBadgeSpec {
  variant: "info" | "warn" | "neutral";
  text: string;
}

function windowBadgeFor(lb: Leaderboard): WindowBadgeSpec {
  if (lb.window_kind === "recurring") {
    return { variant: "info", text: describeCron(lb.cron) };
  }
  if (lb.window_kind === "event") {
    return { variant: "warn", text: "event" };
  }
  // The list page's branch, verbatim with the rest of this helper — including
  // why the length is glued on with a "·" and the schedule keeps its weekday.
  if (lb.window_kind === "recurring_event") {
    const schedule = describeCron(lb.cron);
    return {
      variant: "info",
      text:
        lb.window_minutes != null
          ? `${schedule} · ${formatWindowLength(lb.window_minutes)}`
          : schedule,
    };
  }
  if (lb.window_kind === "all_time") {
    return { variant: "neutral", text: "all-time" };
  }
  // A kind this build predates. Named rather than drawn as all-time — see the
  // list page.
  return { variant: "neutral", text: lb.window_kind };
}

function operatorBadgeFor(lb: Leaderboard): { glyph: string; label: string } {
  switch (lb.operator) {
    case "best":
    case "worst":
      return { glyph: lb.sort === "asc" ? "↑" : "↓", label: lb.operator };
    case "increment":
      return { glyph: "Σ", label: "increment" };
    case "set":
      return { glyph: "=", label: "set" };
    default:
      return { glyph: "·", label: lb.operator };
  }
}

function scopeLabelFor(lb: Leaderboard): string {
  if (lb.scope === "guild_aggregated") {
    return `guild_aggregated (${lb.guild_aggregator ?? "sum"})`;
  }
  return lb.scope;
}

// ──────────────────────────────────────────────────────────────────────────────
// Time / number formatting
// ──────────────────────────────────────────────────────────────────────────────

const ABS_FMT = new Intl.DateTimeFormat("en-US", {
  year: "numeric", month: "short", day: "2-digit",
  hour: "2-digit", minute: "2-digit", hour12: false,
});

/** Short weekday for the progress strip (e.g. "Mon" / "Sun"). */
const WEEKDAY_FMT = new Intl.DateTimeFormat("en-US", { weekday: "short" });

function fmtAbs(iso: string | undefined | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return ABS_FMT.format(d);
}

function fmtNumber(n: number | undefined | null): string {
  if (n === undefined || n === null || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-US");
}

/** Compact humanised diff like "3d 4h", "2h 15m", "5m". Negative → "closed". */
function humaniseUntil(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const secs = Math.floor((d.getTime() - Date.now()) / 1000);
  if (secs <= 0) return "closed";
  const days = Math.floor(secs / 86400);
  const hrs = Math.floor((secs % 86400) / 3600);
  const mins = Math.floor((secs % 3600) / 60);
  if (days > 0) return `${days}d ${hrs}h`;
  if (hrs > 0) return `${hrs}h ${mins}m`;
  return `${mins}m`;
}

/** 0..1 progress through [open_at, close_at]; clamped. NaN→0. */
function cycleProgress(c: Cycle | undefined): number {
  if (!c?.close_at) return 0;
  const start = new Date(c.open_at).getTime();
  const end = new Date(c.close_at).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  const now = Date.now();
  if (now <= start) return 0;
  if (now >= end) return 1;
  return (now - start) / (end - start);
}

function shortId(id: string | undefined, n = 8): string {
  if (!id) return "—";
  return id.replace(/-/g, "").slice(0, n);
}

// ──────────────────────────────────────────────────────────────────────────────
// Tab definitions
// ──────────────────────────────────────────────────────────────────────────────

type TabId = "overview" | "cycles" | "rewards" | "audit" | "settings";
const TAB_ORDER: TabId[] = ["overview", "cycles", "rewards", "audit", "settings"];
const TAB_LABEL: Record<TabId, string> = {
  overview: "Overview",
  cycles: "Cycles",
  rewards: "Rewards",
  audit: "Audit",
  settings: "Settings",
};

function isTabId(v: string | null): v is TabId {
  return v === "overview" || v === "cycles" || v === "rewards" || v === "audit" || v === "settings";
}

// ──────────────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────────────

export function LeaderboardDetailPage() {
  const { id: gameId = "", lbId = "" } = useParams<{ id: string; lbId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const rawTab = searchParams.get("tab");
  const tab: TabId = isTabId(rawTab) ? rawTab : "overview";

  // Project name for the breadcrumb (same pattern as the list page).
  const projectQ = useProject(gameId);

  const lbQ = useQuery({
    queryKey: lbKeys.detail(gameId, lbId),
    queryFn: () => getLeaderboard(gameId, lbId),
    enabled: !!gameId && !!lbId,
  });

  // The project's stat catalogue, off the answer the breadcrumb already reads.
  // Only the Settings tab uses it — a guild_score_source=player_stat board
  // picks its `guild_stat_key` out of it — but it is derived here, above the
  // early returns, because hooks cannot live under a conditional.
  //
  // `undefined` while the request is in flight, and deliberately not `[]`: the
  // form reads an empty array as "this project declares no int stat" and says
  // so on screen, which would be a false accusation against a board that is
  // working perfectly.
  const statsSchema = useMemo(
    () =>
      projectQ.data
        ? normalizeConfig(projectQ.data.config).profile.stats_schema
        : undefined,
    [projectQ.data],
  );

  // === Not-found short-circuit ======================================== //
  if (lbQ.isError && lbQ.error instanceof ApiError && lbQ.error.code === "not_found") {
    return (
      <>
        <PageHeader
          breadcrumb={[
            { label: "All projects", to: "/projects" },
            {
              label: projectQ.data?.name ?? "Project",
              to: `/projects/${gameId}`,
            },
            { label: "Leaderboards", to: `/projects/${gameId}/leaderboards` },
          ]}
          title="Not found"
        />
        <div className={styles.page}>
          <Alert variant="danger" title="Leaderboard not found">
            We couldn't find the leaderboard you were looking for. It may have
            been archived or the link may be incorrect.
          </Alert>
          <div className={styles.errorActions}>
            <Link to={`/projects/${gameId}/leaderboards`} className={styles.backLink}>
              ← Back to leaderboards
            </Link>
          </div>
        </div>
      </>
    );
  }

  const lb = lbQ.data;
  const lbName = lb?.name ?? "Leaderboard";

  // No `title` here: the big name belongs to DetailHead below (mockup
  // leaderboard-detail.html L557–L596), so the header carries crumbs only and
  // the name closes them as the current page.
  const header = (
    <PageHeader
      breadcrumb={[
        { label: "All projects", to: "/projects" },
        {
          label: projectQ.data?.name ?? "Project",
          to: `/projects/${gameId}`,
        },
        { label: "Leaderboards", to: `/projects/${gameId}/leaderboards` },
        { label: lbName },
      ]}
    />
  );

  // === Other error states ============================================= //
  if (lbQ.isError) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <Alert variant="danger" title="Couldn't load leaderboard">
            {lbQ.error instanceof ApiError
              ? describeError(lbQ.error.code)
              : describeError("internal")}
          </Alert>
        </div>
      </>
    );
  }

  // === Loading state (skeleton header) ================================ //
  if (!lb) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <div className={styles.detailHead}>
            <Skeleton width={220} height={24} />
            <div style={{ marginTop: 10 }}>
              <Skeleton width={420} height={12} />
            </div>
          </div>
          <div className={styles.tabBar}>
            {TAB_ORDER.map((id) => (
              <span key={id} className={styles.tab}>
                {TAB_LABEL[id]}
              </span>
            ))}
          </div>
          <div className={styles.tabBody}>
            <Skeleton height={180} />
          </div>
        </div>
      </>
    );
  }

  // === Sticky detail head ============================================= //
  return (
    <>
      {header}
      <div className={styles.page}>
        <DetailHead lb={lb} />
        <TabBar active={tab} />
        <div className={styles.tabBody}>
          {tab === "overview" && (
            <OverviewTab gameId={gameId} lbId={lbId} lb={lb} />
          )}
          {tab === "cycles" && (
            <CyclesTab gameId={gameId} lbId={lbId} lb={lb} />
          )}
          {tab === "rewards" && (
            <RewardsTab gameId={gameId} lbId={lbId} lb={lb} />
          )}
          {tab === "audit" && (
            <AuditTab gameId={gameId} lbId={lbId} lb={lb} />
          )}
          {tab === "settings" && (
            <SettingsTab
              gameId={gameId}
              lbId={lbId}
              lb={lb}
              statsSchema={statsSchema}
              /* Archived or deleted — either way this card is no longer the
                 thing to be looking at, and the list is where the operator
                 came from. */
              onGone={() => navigate(`/projects/${gameId}/leaderboards`)}
            />
          )}
        </div>
      </div>
    </>
  );
}

// === Sticky detail head ============================================== //

function DetailHead({ lb }: { lb: Leaderboard }) {
  const wb = windowBadgeFor(lb);
  return (
    <div className={styles.detailHead}>
      <div className={styles.row1}>
        <h1 className={styles.lbTitle}>{lb.name}</h1>
        <StatusPill status={lb.status} />
        <div className={styles.spacer} />
        <button
          type="button"
          className={styles.kebab}
          aria-label="Leaderboard actions"
          title="Actions (coming soon)"
          disabled
        >
          <MoreVertical size={16} aria-hidden="true" />
        </button>
      </div>
      {lb.description && (
        <div className={styles.secondaryMuted}>{lb.description}</div>
      )}
      <div className={styles.secondary}>
        <code className={styles.miniMono}>{lb.ext_key}</code>
        <span className={styles.sep}>·</span>
        <span className={styles.segVal}>{lb.operator}</span>
        <span className={styles.sep}>·</span>
        <span className={styles.segVal}>{lb.sort}</span>
        <span className={styles.sep}>·</span>
        <span className={styles.segVal}>{wb.text}</span>
        <span className={styles.sep}>·</span>
        <span className={styles.segVal}>{lb.visibility}</span>
        <span className={styles.sep}>·</span>
        {/* The size is what makes "bucket" mean anything: it is the number the
            board splits at, and until now it appeared nowhere on this screen. */}
        <span className={styles.segVal}>
          {lb.segmentation === "tier" && lb.tier_mode
            ? `tier (${lb.tier_mode})`
            : lb.segmentation === "bucket" && lb.bucket_size != null
              ? `bucket (${lb.bucket_size})`
              : lb.segmentation === "personal" && lb.roster_size != null
                ? `personal (${lb.roster_size} per list)`
                : lb.segmentation}
        </span>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: Leaderboard["status"] }) {
  const cls =
    status === "active"
      ? styles.statusActive
      : status === "paused"
        ? styles.statusPaused
        : styles.statusArchived;
  return (
    <span className={`${styles.statusPill} ${cls}`}>
      <span className={styles.statusDot} aria-hidden="true" />
      {status}
    </span>
  );
}

// === Tab bar ========================================================= //

function TabBar({ active }: { active: TabId }) {
  return (
    <div role="tablist" className={styles.tabBar}>
      {TAB_ORDER.map((id) => {
        const isActive = id === active;
        return (
          <Link
            key={id}
            role="tab"
            aria-selected={isActive}
            to={`?tab=${id}`}
            className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}
          >
            {TAB_LABEL[id]}
          </Link>
        );
      })}
    </div>
  );
}

// === Overview tab ==================================================== //

function OverviewTab({
  gameId,
  lbId,
  lb,
}: {
  gameId: string;
  lbId: string;
  lb: Leaderboard;
}) {
  const cyclesQ = useQuery({
    queryKey: lbKeys.cycles(gameId, lbId),
    queryFn: () => listCycles(gameId, lbId),
    enabled: !!gameId && !!lbId,
  });

  const currentCycle: Cycle | undefined = useMemo(() => {
    const cs = cyclesQ.data?.cycles ?? [];
    return cs.find((c) => c.status === "open") ?? cs[0];
  }, [cyclesQ.data]);

  const topQ = useQuery({
    queryKey: lbKeys.top(gameId, lbId, currentCycle?.id ?? "", { limit: 5 }),
    queryFn: () => {
      if (!currentCycle) return Promise.reject(new Error("no current cycle"));
      return listCycleTop(gameId, lbId, currentCycle.id, { limit: 5 });
    },
    enabled: !!currentCycle,
  });

  // Same ?expand=entity as the Audit tab: this card names the same rows, and
  // an id resolved for one of them is resolved for both.
  const auditFilter = useMemo(() => ({ limit: 5, expand: ["entity"] }), []);
  const auditQ = useQuery({
    queryKey: lbKeys.audit(gameId, lbId, currentCycle?.id ?? "", auditFilter),
    queryFn: () => {
      if (!currentCycle) return Promise.reject(new Error("no current cycle"));
      return listAudit(gameId, lbId, currentCycle.id, auditFilter);
    },
    enabled: !!currentCycle,
  });

  // The aggregate behind every segmentation caption on this tab. Constant-size
  // by construction, and not requested at all on boards that have no rooms to
  // count — see useBucketSummary.
  // The CURRENT cycle's shape, not the board's. The two differ for exactly one
  // week — the one running when a bucket board is switched to personal lists —
  // and during it the hero would otherwise report «no segments» about a cycle
  // whose rows are all sitting in rooms.
  const shape = cycleShape(lb, currentCycle);
  const summary = useBucketSummary(gameId, lbId, currentCycle?.id, shape);
  const segments = describeSegments(shape, summary);

  const entries: Entry[] = topQ.data?.entries ?? [];
  const auditRows: AuditEntry[] = auditQ.data?.audit ?? [];

  return (
    <div className={styles.overviewGrid}>
      <HeroCard
        gameId={gameId}
        lb={lb}
        cycle={currentCycle}
        topEntry={entries[0]}
        segments={segments}
        loading={cyclesQ.isLoading}
        error={cyclesQ.isError ? cyclesQ.error : null}
      />
      <TopFiveCard
        gameId={gameId}
        lb={lb}
        cycle={currentCycle}
        entries={entries}
        segments={segments}
        loading={topQ.isLoading}
        error={topQ.isError ? topQ.error : null}
      />
      <ConfigSummaryCard lb={lb} />
      <RecentAuditCard
        gameId={gameId}
        rows={auditRows}
        loading={auditQ.isLoading}
        error={auditQ.isError ? auditQ.error : null}
      />
    </div>
  );
}

/** Render a short inline error blurb inside a card body. Centralised so the
 * three Overview sub-cards (Hero/Top-5/Recent audit) format errors identically.
 */
function describeQueryError(error: unknown): string {
  if (error instanceof ApiError) return describeError(error.code);
  return describeError("internal");
}

// === Card 1 — Hero =================================================== //

function HeroCard({
  gameId,
  lb,
  cycle,
  topEntry,
  segments,
  loading,
  error,
}: {
  gameId: string;
  lb: Leaderboard;
  cycle: Cycle | undefined;
  topEntry: Entry | undefined;
  segments: SegmentDescriptor | null;
  loading: boolean;
  error: unknown;
}) {
  if (error) {
    return (
      <section className={`${styles.card} ${styles.span2}`}>
        <CardHeader title="Current cycle" sub="live · refreshes every 30s" />
        <div className={styles.cardBody}>
          <div className={styles.cardError}>
            Couldn&apos;t load cycles — {describeQueryError(error)}
          </div>
        </div>
      </section>
    );
  }
  if (loading) {
    return (
      <section className={`${styles.card} ${styles.span2}`}>
        <CardHeader title="Current cycle" sub="live · refreshes every 30s" />
        <div className={styles.cardBody}>
          <Skeleton height={120} />
        </div>
      </section>
    );
  }
  if (!cycle) {
    return (
      <section className={`${styles.card} ${styles.span2}`}>
        <CardHeader title="Current cycle" sub="live · refreshes every 30s" />
        <div className={styles.cardBody}>
          <div className={styles.placeholder}>No active cycle.</div>
        </div>
      </section>
    );
  }
  const progress = cycleProgress(cycle);
  const openLabel = WEEKDAY_FMT.format(new Date(cycle.open_at));
  const closeLabel = cycle.close_at
    ? WEEKDAY_FMT.format(new Date(cycle.close_at))
    : "—";
  return (
    <section className={`${styles.card} ${styles.span2}`}>
      <CardHeader title="Current cycle" sub="live · refreshes every 30s" />
      <div className={styles.cardBody}>
        <div className={styles.hero}>
          <div className={styles.heroRow}>
            <span className={styles.heroCid}>{shortId(cycle.id)}</span>
            <CycleStatusPill status={cycle.status} />
            <span className={styles.heroOpened}>
              Opened {fmtAbs(cycle.open_at)} · Closes {fmtAbs(cycle.close_at)}
            </span>
          </div>
          {/* The fourth column exists only on a segmented board; `none` keeps
              the three it always had, down to the grid template. */}
          <div
            className={`${styles.heroStats} ${segments ? styles.heroStats4 : ""}`}
          >
            <div className={styles.heroStat}>
              <div className={styles.heroLbl}>Time until close</div>
              <div className={styles.heroVal} title={fmtAbs(cycle.close_at)}>
                {humaniseUntil(cycle.close_at)}
              </div>
              <div className={styles.heroSub}>≈ {fmtAbs(cycle.close_at)}</div>
            </div>
            <div className={styles.heroStat}>
              <div className={styles.heroLbl}>Participants</div>
              <div className={styles.heroVal}>{fmtNumber(cycle.participants)}</div>
              <div className={styles.heroSub}>across this cycle</div>
            </div>
            {segments && (
              <div className={styles.heroStat}>
                <div className={styles.heroLbl}>Segments</div>
                <div className={styles.heroVal}>{segments.statValue}</div>
                <div className={styles.heroSub}>{segments.statSub}</div>
              </div>
            )}
            <div className={styles.heroStat}>
              <div className={styles.heroLbl}>Top score</div>
              <div className={styles.heroVal}>
                {topEntry ? fmtNumber(topEntry.score) : "—"}
              </div>
              <div className={styles.heroSub}>
                {topEntry ? (
                  <>
                    <EntityLink
                      gameId={gameId}
                      id={topEntry.player.id}
                      name={topEntry.player.display_name}
                      kind={entityKindOf(lb)}
                    />
                    {/* The id rides along BESIDE the name. With no name to
                        show, EntityLink already falls back to that same id —
                        printing it twice is not a caption. */}
                    {topEntry.player.display_name && (
                      <>
                        <span>·</span>
                        <span className={styles.heroSubId}>
                          {shortId(topEntry.player.id)}
                        </span>
                      </>
                    )}
                  </>
                ) : (
                  "—"
                )}
              </div>
            </div>
          </div>
          {cycle.close_at && (
            <div className={styles.progressRow}>
              <span>{openLabel}</span>
              <div className={styles.progress}>
                <div
                  className={styles.progressBar}
                  style={{ width: `${(progress * 100).toFixed(1)}%` }}
                />
              </div>
              <span>{closeLabel}</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function CycleStatusPill({ status }: { status: Cycle["status"] }) {
  const cls =
    status === "open"
      ? styles.cycleOpen
      : status === "closing"
        ? styles.cycleClosing
        : status === "scheduled"
          ? styles.cycleScheduled
          : styles.cycleClosed;
  return (
    <span className={`${styles.statusPill} ${cls}`}>
      <span className={styles.statusDot} aria-hidden="true" />
      {status}
    </span>
  );
}

// === Card 2 — Top-5 ================================================== //

function TopFiveCard({
  gameId,
  lb,
  cycle,
  entries,
  segments,
  loading,
  error,
}: {
  gameId: string;
  lb: Leaderboard;
  cycle: Cycle | undefined;
  entries: Entry[];
  segments: SegmentDescriptor | null;
  loading: boolean;
  error: unknown;
}) {
  // The header used to name the cycle and stop there, which read as "the top of
  // this cycle". On a segmented board it is the top ACROSS the segments.
  const cycleLabel = cycle ? shortId(cycle.id) : "";
  const sub =
    cycleLabel && segments?.scopeLabel
      ? `${cycleLabel} · ${segments.scopeLabel}`
      : cycleLabel;
  return (
    <section className={styles.card}>
      <CardHeader title="Top 5" sub={sub} />
      {error ? (
        <div className={styles.cardBody}>
          <div className={styles.cardError}>
            Couldn&apos;t load top — {describeQueryError(error)}
          </div>
        </div>
      ) : loading ? (
        <div className={styles.cardBody}>
          <Skeleton height={140} />
        </div>
      ) : entries.length === 0 ? (
        <div className={styles.cardBody}>
          <div className={styles.placeholder}>No entries yet.</div>
        </div>
      ) : (
        <>
          {segments && <SegmentNote parts={segments.note} />}
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: 48 }}>#</th>
                <th>Entity</th>
                {segments && <th>{segments.columnLabel}</th>}
                <th className={styles.num}>Score</th>
              </tr>
            </thead>
            <tbody>
              {entries.slice(0, 5).map((e) => {
                const rankCls =
                  e.rank === 1
                    ? styles.rankGold
                    : e.rank === 2
                      ? styles.rankSilver
                      : e.rank === 3
                        ? styles.rankBronze
                        : "";
                return (
                  <tr key={e.player.id}>
                    <td className={`${styles.rank} ${rankCls}`}>{e.rank}</td>
                    <td>
                      <EntityCell
                        gameId={gameId}
                        id={e.player.id}
                        name={e.player.display_name}
                        kind={entityKindOf(lb)}
                        size="sm"
                      />
                    </td>
                    {segments && (
                      <SegmentCell entry={e} kind={segments.kind} />
                    )}
                    <td className={`${styles.num} ${styles.score}`}>
                      {fmtNumber(e.score)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
      <div className={styles.cardFooter}>
        <Link to="?tab=cycles">View full top →</Link>
      </div>
    </section>
  );
}

// === Card 3 — Config summary ========================================= //

function ConfigSummaryCard({ lb }: { lb: Leaderboard }) {
  const wb = windowBadgeFor(lb);
  const op = operatorBadgeFor(lb);
  return (
    <section className={styles.card}>
      <CardHeader
        title="Config summary"
        sub={
          <>
            read-only ·{" "}
            <Link to="?tab=settings" className={styles.cardSubLink}>
              Edit →
            </Link>
          </>
        }
      />
      <div className={styles.cardBody}>
        <div className={styles.summaryGrid}>
          <SummaryRow label="Operator">
            <span className={`${styles.badge} ${styles.badgeNeutral}`}>
              <span className={styles.glyph}>{op.glyph}</span> {op.label}
            </span>
          </SummaryRow>
          <SummaryRow label="Sort">
            <span className={`${styles.badge} ${styles.badgeNeutral} ${styles.badgeMono}`}>
              {lb.sort}
            </span>
          </SummaryRow>
          <SummaryRow label="Window">
            <span
              className={`${styles.badge} ${
                wb.variant === "info"
                  ? styles.badgeInfo
                  : wb.variant === "warn"
                    ? styles.badgeWarn
                    : styles.badgeNeutral
              }`}
            >
              {lb.window_kind}
            </span>
            {/* TODO(window-tz): show timezone next to cron once Leaderboard
                DTO exposes a timezone column (Go-truth currently has none). */}
            {lb.cron && <code className={styles.miniCode}>{lb.cron}</code>}
            {/* The second half of a recurring_event schedule, in the same
                shape as the first: on this board the cron alone says only
                when a window OPENS. */}
            {lb.window_minutes != null && (
              <code className={styles.miniCode}>
                {formatWindowLength(lb.window_minutes)}
              </code>
            )}
          </SummaryRow>
          <SummaryRow label="Scope">
            <span className={`${styles.badge} ${styles.badgeNeutral}`}>
              {scopeLabelFor(lb)}
            </span>
          </SummaryRow>
          <SummaryRow label="Segmentation">
            <span className={`${styles.badge} ${styles.badgeNeutral}`}>
              {lb.segmentation}
            </span>
            {/* On a bucket board the size IS the segmentation; on a tier board
                it is the second cut, and `off` says the leagues compete whole. */}
            {lb.segmentation === "bucket" && lb.bucket_size != null && (
              <span className={styles.summaryMuted}>
                size: <code className={styles.miniCode}>{lb.bucket_size}</code>
              </span>
            )}
            {lb.segmentation === "tier" && lb.tier_mode && (
              <span className={styles.summaryMuted}>
                tier_mode: <code className={styles.miniCode}>{lb.tier_mode}</code>{" "}
                · rooms:{" "}
                <code className={styles.miniCode}>
                  {lb.bucket_size ?? "off"}
                </code>
              </span>
            )}
            {/* Not «size» — nothing is divided into lists of ten here. Every
                player simply gets their own ten rows. */}
            {lb.segmentation === "personal" && lb.roster_size != null && (
              <span className={styles.summaryMuted}>
                per list: <code className={styles.miniCode}>{lb.roster_size}</code>
              </span>
            )}
          </SummaryRow>
          <SummaryRow label="Visibility">
            <span className={`${styles.badge} ${styles.badgeNeutral}`}>
              {lb.visibility}
            </span>
          </SummaryRow>
          <SummaryRow label="Submit policy">
            <span className={`${styles.badge} ${styles.badgeNeutral} ${styles.badgeMono}`}>
              {lb.submit_policy}
            </span>
          </SummaryRow>
          <SummaryRow label="Realtime">
            {lb.realtime ? (
              <span className={`${styles.badge} ${styles.badgeOk}`}>on</span>
            ) : (
              <span className={`${styles.badge} ${styles.badgeNeutral}`}>off</span>
            )}
            {lb.realtime && (
              <span className={styles.summaryMuted}>rank.changed WS</span>
            )}
          </SummaryRow>
          {/* Next to Realtime because it is the same kind of fact: a toggle
              that changes what the CLIENT is offered, not how the board is
              scored. The note says which half stays worldwide, since that is
              the question the flag raises — a second view is easily mistaken
              for a second competition.

              Rendered only where the board could HAVE one: on a guild_internal
              or segmented board the CHECK refuses the toggle and the form shows
              no switch, so a row reading «off» would send an operator hunting
              for something that is not there. */}
          {countrySplitAllowed(lb) && (
            <SummaryRow label="Country split">
              {lb.country_split ? (
                <span className={`${styles.badge} ${styles.badgeOk}`}>on</span>
              ) : (
                <span className={`${styles.badge} ${styles.badgeNeutral}`}>
                  off
                </span>
              )}
              {lb.country_split && (
                <span className={styles.summaryMuted}>
                  second tab per country · rewards stay worldwide
                </span>
              )}
            </SummaryRow>
          )}
        </div>
      </div>
    </section>
  );
}

function SummaryRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.summaryRow}>
      <div className={styles.summaryKey}>{label}</div>
      <div className={styles.summaryValue}>{children}</div>
    </div>
  );
}

// === Card 4 — Recent audit =========================================== //

function RecentAuditCard({
  gameId,
  rows,
  loading,
  error,
}: {
  gameId: string;
  rows: AuditEntry[];
  loading: boolean;
  error: unknown;
}) {
  return (
    <section className={`${styles.card} ${styles.span2}`}>
      <CardHeader title="Recent audit" sub="last 5 events" />
      {error ? (
        <div className={styles.cardBody}>
          <div className={styles.cardError}>
            Couldn&apos;t load audit — {describeQueryError(error)}
          </div>
        </div>
      ) : loading ? (
        <div className={styles.cardBody}>
          <Skeleton height={120} />
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.cardBody}>
          <div className={styles.placeholder}>No audit events yet.</div>
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ width: 100 }}>When</th>
              <th style={{ width: 70 }}>Channel</th>
              <th style={{ width: 190 }}>Actor</th>
              <th style={{ width: 200 }}>Entity</th>
              <th>Score</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 5).map((row) => (
              <tr key={row.id} className={styles.auditRow}>
                <td className={styles.muted} title={fmtAbs(row.created_at)}>
                  {formatRelativeShort(row.created_at)}
                </td>
                <td>
                  <ChannelBadge channel={row.channel} />
                </td>
                <td>
                  <ActorCell row={row} />
                </td>
                <td>
                  <EntityCell
                    gameId={gameId}
                    id={row.entity_id}
                    name={row.entity_name}
                    kind={row.entity_kind}
                  />
                </td>
                <td>
                  {/* The same cell the Audit tab draws — before → after,
                      with what arrived trailing it where the two differ. */}
                  <ScoreFlow row={row} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className={styles.cardFooter}>
        <Link to="?tab=audit">View all →</Link>
      </div>
    </section>
  );
}

function ChannelBadge({ channel }: { channel: AuditEntry["channel"] }) {
  const cls =
    channel === "client"
      ? styles.chClient
      : channel === "s2s"
        ? styles.chS2s
        : styles.chAdmin;
  return <span className={`${styles.chBadge} ${cls}`}>{channel}</span>;
}

// === Card chrome ===================================================== //

function CardHeader({
  title,
  sub,
}: {
  title: string;
  sub?: React.ReactNode;
}) {
  return (
    <div className={styles.cardHeader}>
      <h3 className={styles.cardTitle}>{title}</h3>
      {sub && <span className={styles.cardSub}>{sub}</span>}
    </div>
  );
}
